import React, { memo, useId, useMemo, useRef } from 'react';

import { marked, type Tokens } from 'marked';
import ReactMarkdown, { Components } from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import remarkGfm from 'remark-gfm';

import { CodeBlock, CodeBlockCode } from '@/components/core/CodeBlock';
import { libraryDocumentHref } from '@/components/features/ChatContainer/utils/libraryDocuments';
import { cn } from '@/lib/utils';

import { AttachmentFileLink } from './AttachmentFileLink';
import {
  parseLibraryPageHref,
  parsePageCitationHref,
  usePageCitation,
} from './pageCitation';
import {
  attachmentKind,
  useWorksheetPhotos,
  worksheetPhotoFromUrl,
  type AttachmentFile,
} from './worksheetPhoto';
import { WorksheetPhotoLink } from './WorksheetPhotoThumb';

export type MarkdownProps = {
  children: string;
  id?: string;
  className?: string;
  components?: Partial<Components>;
  revealStreaming?: boolean;
};

function parseMarkdownIntoBlocks(markdown: string): string[] {
  const tokens = marked.lexer(markdown);
  return tokens.map(token => token.raw);
}

function extractLanguage(className?: string): string {
  if (!className) return 'plaintext';
  const match = className.match(/language-(\w+)/);
  return match ? match[1] : 'plaintext';
}

const MARKDOWN_TABLE_MAX_BODY_ROWS = 75;

/** Cap GFM table body rows before parse so huge Connie replies stay renderable on mobile. */
function truncateMarkdownTables(
  markdown: string,
  maxBodyRows = MARKDOWN_TABLE_MAX_BODY_ROWS
): string {
  const lines = markdown.split('\n');
  const output: string[] = [];
  let bodyRows = 0;
  let inTable = false;
  let sawSeparator = false;
  let omitted = 0;

  const flushOmitted = () => {
    if (omitted <= 0) return;
    output.push(`| …and ${omitted.toLocaleString()} more rows |`);
    omitted = 0;
  };

  for (const line of lines) {
    const isTableRow = /^\s*\|.*\|\s*$/.test(line);
    const isSeparator = /^\s*\|?\s*:?-{3,}/.test(line);

    if (!inTable) {
      if (isTableRow) {
        inTable = true;
        sawSeparator = false;
        bodyRows = 0;
        omitted = 0;
      }
      output.push(line);
      continue;
    }

    if (!isTableRow) {
      flushOmitted();
      inTable = false;
      sawSeparator = false;
      output.push(line);
      continue;
    }

    if (!sawSeparator && isSeparator) {
      sawSeparator = true;
      output.push(line);
      continue;
    }

    if (!sawSeparator) {
      output.push(line);
      continue;
    }

    bodyRows += 1;
    if (bodyRows <= maxBodyRows) {
      output.push(line);
    } else {
      omitted += 1;
    }
  }

  flushOmitted();
  return output.join('\n');
}

const LIST_CLASS = 'pl-0 list-none mt-0 mb-5';
const LIST_ITEM_CLASS = 'flex items-baseline my-2 text-sm leading-6';
const BLOCKQUOTE_CLASS =
  'mt-0 mb-5 border-l-4 border-purple-500 text-fg-default pl-4 italic';
const LIST_BULLET_CLASS =
  'w-3 md:w-6 text-right mr-2 font-semibold text-fg-default shrink-0';
const LIST_NUMBER_CLASS =
  'w-6 text-right mr-2 font-semibold text-fg-default shrink-0';

const HEADING_CLASS = {
  1: 'text-2xl leading-9 font-bold text-fg-default mb-6',
  2: 'text-xl leading-8 font-bold text-fg-default [&:not(:first-child)]:pt-6 mb-4 [&:not(:first-child)]:border-t',
  3: 'text-base leading-7 font-semibold text-fg-default [&:not(:first-child)]:mt-2 mb-4',
  4: 'text-sm leading-6 font-semibold text-fg-default [&:not(:first-child)]:mt-2 mb-2',
} as const;

export const LINK_CLASS =
  'text-[var(--chakra-colors-link-font)] underline decoration-[var(--chakra-colors-link-decoration)] decoration-1 underline-offset-2 hover:text-[var(--chakra-colors-link-font-hover)] hover:decoration-current';

const CITATION_CLASS =
  'mx-0.5 inline-flex cursor-pointer items-center rounded-md bg-[var(--chakra-colors-link-citation-bg)] px-1.5 align-baseline text-xs font-medium leading-5 text-[var(--chakra-colors-link-font)] whitespace-nowrap hover:bg-[var(--chakra-colors-link-citation-bg-hover)] hover:text-[var(--chakra-colors-link-font-hover)] focus-visible:outline focus-visible:outline-2 focus-visible:outline-[var(--chakra-colors-link-decoration)]';

const citationLabel = (children: React.ReactNode) =>
  React.Children.toArray(children)
    .filter(child => typeof child === 'string' || typeof child === 'number')
    .join('')
    .replace(/^\[|\]$/g, '')
    .trim();

function attachmentFile(href: string, label?: string): AttachmentFile | null {
  const kind = attachmentKind(href);
  if (kind !== 'pdf' && kind !== 'file') return null;
  const text = label?.trim();
  return {
    url: href,
    kind,
    label: text && text !== href ? text : undefined,
  };
}

function MarkdownAttachment({ href, label }: { href: string; label?: string }) {
  const photos = useWorksheetPhotos();
  if (!photos) return null;

  const photo = worksheetPhotoFromUrl(href, label);
  if (photo) {
    photos.registerPhoto(photo);
    return (
      <WorksheetPhotoLink
        photo={photo}
        className={LINK_CLASS}
        onOpen={() => photos.openPhoto(photo)}
      >
        {label?.trim() || 'View photo'}
      </WorksheetPhotoLink>
    );
  }

  const file = attachmentFile(href, label);
  if (file) return <AttachmentFileLink file={file} />;
  return null;
}

function MarkdownImage({
  src,
  alt,
}: {
  src?: string | null;
  alt?: string | null;
}) {
  const photos = useWorksheetPhotos();
  if (!src) return null;
  if (!photos) return <img src={src} alt={alt ?? ''} />;
  if (!attachmentKind(src)) return <span>{alt || src}</span>;

  return <MarkdownAttachment href={src} label={alt ?? undefined} />;
}

function MarkdownLink({
  href,
  children,
}: {
  href?: string;
  children?: React.ReactNode;
}) {
  const pageCitation = usePageCitation();
  const photos = useWorksheetPhotos();
  const page = parsePageCitationHref(href);
  const libraryPage = parseLibraryPageHref(href);

  if (libraryPage) {
    // Connie chat: the Library Document opens at that page, in a new tab, as the
    // Library chip does (VH3_CONNECT #549).
    const text = citationLabel(children);
    return (
      <a
        href={libraryDocumentHref(libraryPage.documentId, libraryPage.page)}
        target='_blank'
        rel='noopener noreferrer'
        className={CITATION_CLASS}
        title={text || undefined}
        aria-label={`Open page ${libraryPage.page} in the Library${text ? ` (${text})` : ''}`}
        data-cy='library-page-citation'
      >
        p. {libraryPage.page}
      </a>
    );
  }

  if (page !== null) {
    if (!pageCitation) return <>{children}</>;
    const text = citationLabel(children);
    const label = text === `p. ${page}` ? '' : text;
    return (
      <button
        type='button'
        className={CITATION_CLASS}
        title={label || undefined}
        aria-label={`Open page ${page} in the document viewer${label ? ` (${label})` : ''}`}
        onClick={() => pageCitation.onOpenPage(page)}
      >
        p. {page}
      </button>
    );
  }

  if (photos && href && attachmentKind(href)) {
    const text = citationLabel(children);
    return <MarkdownAttachment href={href} label={text || undefined} />;
  }

  return (
    <a
      href={href}
      className={LINK_CLASS}
      onClick={e => {
        e.preventDefault();
      }}
    >
      {children}
    </a>
  );
}

const buildMarkdownComponents = (blockClass?: string): Partial<Components> => ({
  code: function CodeComponent({ className, children, ...props }) {
    const isInline =
      !props.node?.position?.start.line ||
      props.node?.position?.start.line === props.node?.position?.end.line;

    if (isInline) {
      return (
        <span
          className={cn('rounded-sm px-1 font-mono text-base', className)}
          {...props}
        >
          {children}
        </span>
      );
    }

    const language = extractLanguage(className);

    return (
      <CodeBlock className={className}>
        <CodeBlockCode
          code={children as string}
          language={language}
          theme='nord'
        />
      </CodeBlock>
    );
  },
  strong: function StrongComponent({ children }) {
    return (
      <strong className='font-semibold text-fg-default'>{children}</strong>
    );
  },
  pre: function PreComponent({ children }) {
    return <>{children}</>;
  },
  p: ({ children }) => <p className={blockClass}>{children}</p>,
  h1: ({ children }) => (
    <h1 className={cn(HEADING_CLASS[1], blockClass)}>{children}</h1>
  ),
  h2: ({ children }) => (
    <h2 className={cn(HEADING_CLASS[2], blockClass)}>{children}</h2>
  ),
  h3: ({ children }) => (
    <h3 className={cn(HEADING_CLASS[3], blockClass)}>{children}</h3>
  ),
  h4: ({ children }) => (
    <h4 className={cn(HEADING_CLASS[4], blockClass)}>{children}</h4>
  ),
  a: ({ children, href }) => (
    <MarkdownLink href={href}>{children}</MarkdownLink>
  ),
  img: ({ src, alt }) => <MarkdownImage src={src} alt={alt} />,
  blockquote: ({ children }) => (
    <blockquote className={cn(BLOCKQUOTE_CLASS, blockClass)}>
      {children}
    </blockquote>
  ),
  ul: ({ children }) => {
    const items = React.Children.toArray(children).filter(
      child => typeof child === 'object'
    );
    return (
      <ul className={cn(LIST_CLASS, blockClass)}>
        {items.map((child, index) => (
          <li className={LIST_ITEM_CLASS} key={index}>
            <span className={LIST_BULLET_CLASS}>{'•'}</span>
            <div className='list-item-content w-full'>{child}</div>
          </li>
        ))}
      </ul>
    );
  },
  ol: ({ children }) => {
    const items = React.Children.toArray(children).filter(
      child => typeof child === 'object'
    );
    return (
      <ol className={cn(LIST_CLASS, blockClass)}>
        {items.map((child, index) => (
          <li className={LIST_ITEM_CLASS} key={index}>
            <span className={LIST_NUMBER_CLASS}>{index + 1}.</span>
            <div className='list-item-content w-full'>{child}</div>
          </li>
        ))}
      </ol>
    );
  },
  li: ({ children }) => {
    return <>{children}</>;
  },
  table: ({ children }) => {
    return (
      <div
        className={cn(
          'mb-5 min-w-0 max-w-full overflow-hidden rounded-md border bg-muted',
          blockClass
        )}
      >
        <div className='min-w-0 w-full overflow-x-auto p-4'>
          <table className='!my-0 w-full markdown-table'>{children}</table>
        </div>
      </div>
    );
  },
});

const INITIAL_COMPONENTS = buildMarkdownComponents();
const BLOCK_IN_COMPONENTS = buildMarkdownComponents('chat-block-in');

/** List items render inline, so drop the paragraph wrapper marked adds. */
const LIST_ITEM_COMPONENTS: Partial<Components> = {
  ...INITIAL_COMPONENTS,
  p: ({ children }) => <>{children}</>,
};

/**
 * Cap sections revealed in a single commit. A throttled dump can land a whole
 * report at once, and each reveal is its own compositor layer.
 */
const MAX_REVEALS_PER_COMMIT = 8;

const LIST_BLOCK_RE = /^\s{0,3}([-*+]|\d{1,9}[.)])\s/;

function isListBlock(content: string): boolean {
  return LIST_BLOCK_RE.test(content);
}

/**
 * Tracks which indexes have been rendered before, so only newly arrived ones
 * reveal — and never more than `MAX_REVEALS_PER_COMMIT` at a time.
 */
function useRevealTracker(live: boolean) {
  const decidedRef = useRef(new Map<number, boolean>());
  const countRef = useRef(0);

  const reset = (total: number) => {
    if (total < countRef.current) decidedRef.current.clear();
    countRef.current = total;
  };

  const decide = (index: number, budget: { left: number }) => {
    const decided = decidedRef.current.get(index);
    if (decided !== undefined) return decided;

    const reveal = live && budget.left > 0;
    if (reveal) budget.left -= 1;
    decidedRef.current.set(index, reveal);
    return reveal;
  };

  return { reset, decide };
}

const MemoizedMarkdownBlock = memo(
  function MarkdownBlock({
    content,
    components = INITIAL_COMPONENTS,
  }: {
    content: string;
    components?: Partial<Components>;
  }) {
    return (
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        rehypePlugins={[rehypeRaw]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    );
  },
  function propsAreEqual(prevProps, nextProps) {
    return prevProps.content === nextProps.content;
  }
);

MemoizedMarkdownBlock.displayName = 'MemoizedMarkdownBlock';

/**
 * Renders a list one item at a time so each bullet that arrives gets its own
 * reveal, instead of the whole list resolving on its first item.
 */
function ListBlock({
  content,
  live,
  reveal,
}: {
  content: string;
  live: boolean;
  reveal: boolean;
}) {
  const token = useMemo(() => marked.lexer(content)[0], [content]);
  const tracker = useRevealTracker(live);

  if (!token || token.type !== 'list') {
    return (
      <MemoizedMarkdownBlock
        content={content}
        components={reveal ? BLOCK_IN_COMPONENTS : INITIAL_COMPONENTS}
      />
    );
  }

  const list = token as Tokens.List;
  const ListTag = list.ordered ? 'ol' : 'ul';
  const start = Number(list.start ?? 1);
  const budget = { left: MAX_REVEALS_PER_COMMIT };
  tracker.reset(list.items.length);

  return (
    <ListTag className={LIST_CLASS}>
      {list.items.map((item, index) => (
        <li
          key={index}
          className={cn(
            LIST_ITEM_CLASS,
            tracker.decide(index, budget) && 'chat-block-in'
          )}
        >
          <span
            className={list.ordered ? LIST_NUMBER_CLASS : LIST_BULLET_CLASS}
          >
            {list.ordered ? `${start + index}.` : '•'}
          </span>
          <div className='list-item-content w-full'>
            <MemoizedMarkdownBlock
              content={item.text}
              components={LIST_ITEM_COMPONENTS}
            />
          </div>
        </li>
      ))}
    </ListTag>
  );
}

function MarkdownComponent({
  children,
  id,
  className,
  components = INITIAL_COMPONENTS,
  revealStreaming = false,
}: MarkdownProps) {
  const generatedId = useId();
  const blockId = id ?? generatedId;
  const blocks = useMemo(
    () => parseMarkdownIntoBlocks(truncateMarkdownTables(children)),
    [children]
  );

  // Each block reveals once, when it first appears. The stream itself paces
  // this — no second clock — so text is never held back or half-rendered.
  const isDefaultComponents = components === INITIAL_COMPONENTS;
  const live = revealStreaming && isDefaultComponents;
  const tracker = useRevealTracker(live);
  const budget = { left: MAX_REVEALS_PER_COMMIT };
  tracker.reset(blocks.length);

  return (
    <div className={className}>
      {blocks.map((block, index) => {
        const key = `${blockId}-block-${index}`;
        const reveal = tracker.decide(index, budget);

        if (isDefaultComponents && isListBlock(block)) {
          return (
            <ListBlock key={key} content={block} live={live} reveal={reveal} />
          );
        }

        return (
          <MemoizedMarkdownBlock
            key={key}
            content={block}
            components={reveal ? BLOCK_IN_COMPONENTS : components}
          />
        );
      })}
    </div>
  );
}

const Markdown = memo(MarkdownComponent);
Markdown.displayName = 'Markdown';

export { Markdown };
